import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import {
  RentVisitFeedbackBody,
  createVisitFeedbackEndpoint,
  updateVisitStateEndpoint,
} from '../services/dashboardService';
import {
  deleteVisitEndpoint,
  getCancellationReasons,
} from '../../properties/services/propertyService';
import { useState } from 'react';

// Campos que el backend puede reportar como inválidos (`data.detail`).
const FEEDBACK_FIELD_LABELS: Record<string, string> = {
  logistics_issue: 'si hubo un problema logístico',
  move_in_timeframe: 'cuándo necesita mudarse el inquilino',
  interest_level: 'el nivel de interés',
  objections: 'las objeciones',
  property_condition: 'cómo encontraste la propiedad',
  owner_recommendation: 'la recomendación al propietario',
};

// Convierte el detalle técnico en inglés del backend en un mensaje legible.
const feedbackErrorMessage = (detail: unknown): string | undefined => {
  if (typeof detail !== 'string' || !detail) return undefined;
  const field = Object.keys(FEEDBACK_FIELD_LABELS).find((key) =>
    detail.includes(key),
  );
  if (!field) return undefined;
  const label = FEEDBACK_FIELD_LABELS[field];
  if (detail.includes('required')) return `Falta indicar ${label}.`;
  if (detail.includes('not allowed') || detail.includes('invalid'))
    return `Este formulario no admite ${label}. Recarga la página e inténtalo de nuevo.`;
  return `Revisa ${label}.`;
};

// Acciones del Proper sobre una visita de Renta/Secondary: las mismas que ya
// usa su dashboard (marcar como realizada y cancelar).
const useProperRentVisitActions = () => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);

  const doMarkAsDone = async (visitId: string): Promise<number> => {
    const { status } = await callEndpoint(
      updateVisitStateEndpoint(visitId, true, token),
    );
    return status;
  };

  // Feedback (Renta/Venta) antes de cerrar la visita. 409 = ya registrado.
  const doSendFeedback = async (
    visitId: string,
    body: RentVisitFeedbackBody,
  ): Promise<{ ok: boolean; message?: string }> => {
    const { status, data } = await callEndpoint(
      createVisitFeedbackEndpoint(visitId, token, body),
    );
    if (status === 201 || status === 200 || status === 409) return { ok: true };
    if (status === 404)
      return {
        ok: false,
        message: 'La visita no existe o no está asignada a este Proper.',
      };
    const detail = data?.data?.detail;
    return {
      ok: false,
      message:
        feedbackErrorMessage(detail) ||
        'No se pudo guardar el feedback. Revisa los datos e intenta de nuevo.',
    };
  };

  const doCancelVisit = async (
    visitId: string,
    reason: string,
    explainReason: string,
  ): Promise<number> => {
    const { status } = await callEndpoint(
      deleteVisitEndpoint(visitId, token, reason, explainReason),
    );
    return status;
  };

  const doLoadCancellationReasons = async () => {
    try {
      const { data } = await callEndpoint(getCancellationReasons(token));
      setCancellationReasons(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCancellationReasons([]);
    }
  };

  return {
    cancellationReasons,
    doMarkAsDone,
    doSendFeedback,
    doCancelVisit,
    doLoadCancellationReasons,
  };
};

export default useProperRentVisitActions;
